import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import serviceCatalog from '../../data/services/servicesData.json';
import { CONTACT_ENDPOINT } from '../../config/contact';

const SERVICES = [...serviceCatalog.map(item => item.title), 'Not sure yet'];
const REQUESTS = ['Quote request', 'Sample edit request', 'Quote + sample edit'];

function validate(values) {
  const errors = {};
  if (!REQUESTS.includes(values.requestType)) errors.requestType = 'Please choose a request type.';
  if (!values.name.trim()) errors.name = 'Please enter your name.';
  if (!values.email.trim()) errors.email = 'Please enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) errors.email = 'Please enter a valid email address.';
  if (!SERVICES.includes(values.service)) errors.service = 'Please choose a service.';
  if (values.referenceLink.trim()) {
    try {
      const url = new URL(values.referenceLink.trim());
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid link');
    } catch {
      errors.referenceLink = 'Please enter a link beginning with https:// or http://.';
    }
  }
  if (!values.message.trim()) errors.message = 'Please add a few details about your project.';
  return errors;
}

function FieldError({ name, errors }) {
  return errors[name] ? <p id={`${name}-error`} className="inquiry-field-error">{errors[name]}</p> : null;
}

export default function ContactForm() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialRequest = params.get('request') === 'sample' ? 'Sample edit request' : 'Quote request';
  const formRef = useRef(null);
  const inFlight = useRef(false);
  const [values, setValues] = useState({
    requestType: initialRequest,
    name: '', email: '',
    service: serviceCatalog.find(item => item.link === params.get('service'))?.title || '',
    imageCount: '', deadline: '', referenceLink: '', message: '', _gotcha: ''
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const [sending, setSending] = useState(false);

  const fieldProps = name => ({
    'aria-invalid': errors[name] ? 'true' : undefined,
    'aria-describedby': errors[name] ? `${name}-error` : undefined
  });
  const handleChange = event => {
    const { name, value } = event.target;
    const next = { ...values, [name]: value };
    setValues(next);
    if (errors[name]) setErrors(current => ({ ...current, [name]: validate(next)[name] }));
    if (status.message) setStatus({ type: '', message: '' });
  };
  const handleSubmit = async event => {
    event.preventDefault();
    if (inFlight.current || values._gotcha) return;
    setStatus({ type: '', message: '' });
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const first = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => formRef.current?.elements.namedItem(first)?.focus());
      return;
    }

    inFlight.current = true;
    setSending(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    const payload = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()]));
    payload.subject = `${payload.requestType} - ${payload.name}`;
    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result || result.ok === false || result.errors?.length) {
        throw new Error('Inquiry not accepted');
      }
      setStatus({ type: 'success', message: 'Inquiry sent. Thank you.' });
      setValues(current => ({ ...current, name: '', email: '', service: '', imageCount: '', deadline: '', referenceLink: '', message: '', _gotcha: '' }));
      setErrors({});
    } catch {
      setStatus({ type: 'error', message: "Your inquiry couldn't be sent. Please try again." });
    } finally {
      window.clearTimeout(timeout);
      inFlight.current = false;
      setSending(false);
    }
  };

  return <form ref={formRef} onSubmit={handleSubmit} noValidate id="cf-1" className="contact-form inquiry-form" aria-busy={sending}>
    <div className="form-intro">
      <p>Tell me a little about your project. You can request a quote, ask for a sample edit, or do both.</p>
      <p className="required-note">Fields marked * are required.</p>
    </div>
    <fieldset className="inquiry-fields" disabled={sending}>
      <div className="form-group form-group-lg">
        <label className="form-label-lg" htmlFor="requestType">What would you like to do? *</label>
        <select id="requestType" name="requestType" className="form-lg" required value={values.requestType} onChange={handleChange} {...fieldProps('requestType')}>
          {REQUESTS.map(request => <option key={request}>{request}</option>)}
        </select>
        <FieldError name="requestType" errors={errors} />
      </div>
      <div className="form-group form-group-lg"><div className="row">
        <div className="col-md-6">
          <label className="form-label-lg" htmlFor="contact-name">Name *</label>
          <input id="contact-name" name="name" type="text" className="form-lg" placeholder="Your name" autoComplete="name" required value={values.name} onChange={handleChange} {...fieldProps('name')} />
          <FieldError name="name" errors={errors} />
        </div>
        <div className="col-md-6">
          <label className="form-label-lg" htmlFor="contact-email">Email *</label>
          <input id="contact-email" name="email" type="email" className="form-lg" placeholder="you@example.com" autoComplete="email" required value={values.email} onChange={handleChange} {...fieldProps('email')} />
          <FieldError name="email" errors={errors} />
        </div>
      </div></div>
      <div className="form-group form-group-lg">
        <label className="form-label-lg" htmlFor="service">Service *</label>
        <select id="service" name="service" className="form-lg" required value={values.service} onChange={handleChange} {...fieldProps('service')}>
          <option value="">Choose a service</option>
          {SERVICES.map(service => <option key={service} value={service}>{service}</option>)}
        </select>
        <FieldError name="service" errors={errors} />
      </div>
      <div className="form-group form-group-lg"><div className="row">
        <div className="col-md-6">
          <label className="form-label-lg" htmlFor="imageCount">Approximate image count</label>
          <input id="imageCount" name="imageCount" className="form-lg" type="text" placeholder="e.g. 250 images" value={values.imageCount} onChange={handleChange} />
        </div>
        <div className="col-md-6">
          <label className="form-label-lg" htmlFor="deadline">Deadline / preferred date</label>
          <input id="deadline" name="deadline" className="form-lg" type="text" placeholder="e.g. within 3 days" value={values.deadline} onChange={handleChange} />
        </div>
      </div></div>
      <div className="form-group form-group-lg">
        <label className="form-label-lg" htmlFor="referenceLink">Reference images or file-share link</label>
        <input id="referenceLink" name="referenceLink" className="form-lg" type="url" placeholder="https://... (optional)" value={values.referenceLink} onChange={handleChange} {...fieldProps('referenceLink')} />
        <FieldError name="referenceLink" errors={errors} />
      </div>
      <div className="form-group form-group-lg">
        <label className="form-label-lg" htmlFor="message">Project details *</label>
        <textarea id="message" name="message" className="form-lg" rows="5" placeholder="Describe the editing style, deliverables, deadline, or sample you have in mind..." required value={values.message} onChange={handleChange} {...fieldProps('message')} />
        <FieldError name="message" errors={errors} />
      </div>
      <div className="form-honeypot" aria-hidden="true">
        <label htmlFor="_gotcha">Leave this field empty</label>
        <input id="_gotcha" name="_gotcha" tabIndex="-1" autoComplete="off" value={values._gotcha} onChange={handleChange} />
      </div>
    </fieldset>
    {status.message && <div className={`form-status form-status-${status.type}`} role={status.type === 'error' ? 'alert' : 'status'}>{status.message}</div>}
    <div className="form-submit-row">
      <button type="submit" className="btn rg-inquiry-submit text-uppercase" disabled={sending}>{sending ? 'Sending...' : 'Send inquiry'}</button>
    </div>
  </form>;
}
